import type { ReactElement } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import type { Channel, CommunityStructure } from "@atrium/shared";
import { api, errorMessage } from "../../lib/api";
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuSeparator, ContextMenuTrigger } from "../../components/ui/ContextMenu";
import { confirm } from "../../components/ui/Dialog";
import { toast } from "../../components/ui/Toast";
import { IconCheckRead, IconIdCard, IconLink, IconSettings, IconTrash } from "../../components/icons";
import { useMe } from "../auth/AuthProvider";
import { copyText } from "../posts/usePostActions";

/** Right-click (long-press) menu for a channel in the sidebar. `children` is the row element. */
export function ChannelContextMenu({
  channel,
  canEdit,
  canDelete,
  active,
  children,
}: {
  channel: Channel;
  canEdit: boolean;
  canDelete: boolean;
  active: boolean;
  children: ReactElement;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const me = useMe();
  const key = ["structure", channel.communityId];

  const markRead = async () => {
    try {
      await api.post(`/api/channels/${channel.id}/read`, {});
      qc.setQueryData<CommunityStructure>(key, (old) => old && { ...old, channels: old.channels.map((c) => (c.id === channel.id ? { ...c, unread: false, mentionCount: 0 } : c)) });
      void qc.invalidateQueries({ queryKey: ["my-communities"] });
      void qc.invalidateQueries({ queryKey: ["unread"] });
    } catch (e) {
      toast.error(errorMessage(e, "Couldn't mark as read"));
    }
  };

  const copyLink = async () => {
    await copyText(`${window.location.origin}/c/${channel.communityId}/${channel.id}`);
    toast.show("Link copied");
  };

  const remove = async () => {
    const ok = await confirm({
      title: `Delete ${channel.type === "voice" ? "" : "#"}${channel.name}?`,
      description: "All messages in this channel will be permanently deleted. This can't be undone.",
      confirmLabel: "Delete channel",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.del(`/api/channels/${channel.id}`);
      await qc.invalidateQueries({ queryKey: key });
      if (active) navigate(`/c/${channel.communityId}`, { replace: true });
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem icon={IconCheckRead} disabled={!channel.unread && !channel.mentionCount} onSelect={() => void markRead()}>
          Mark as read
        </ContextMenuItem>
        <ContextMenuItem icon={IconLink} onSelect={() => void copyLink()}>
          Copy link
        </ContextMenuItem>
        {me.preferences.developerMode && (
          <ContextMenuItem
            icon={IconIdCard}
            onSelect={() => void copyText(channel.id).then(() => toast.show("Channel ID copied"))}
          >
            Copy channel ID
          </ContextMenuItem>
        )}
        {(canEdit || canDelete) && <ContextMenuSeparator />}
        {canEdit && (
          <ContextMenuItem icon={IconSettings} onSelect={() => navigate(`/c/${channel.communityId}/${channel.id}/settings`)}>
            Edit channel
          </ContextMenuItem>
        )}
        {canDelete && (
          <ContextMenuItem icon={IconTrash} danger onSelect={() => setTimeout(() => void remove(), 0)}>
            Delete channel
          </ContextMenuItem>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
}
